import { useEffect, useMemo, useState } from "react";
import { useI18n } from "@wato/i18n/react";
import { hubFetch } from "../ai/hub/sections/hubFetch";

export interface ContactsGrowthView {
  readonly month: string;
  readonly timezone: string;
  readonly days: readonly { date: string; created: number; optedOut: number }[];
  readonly totalCreated: number;
  readonly totalOptedOut: number;
}

/** OWN-150a: new vs opted-out contacts for a month, with a daily trend (bars). */
export function HomeContactsGrowth({ workspaceId }: { readonly workspaceId: string }) {
  const { t, locale } = useI18n();
  const [month, setMonth] = useState(() => new Date().toISOString().slice(0, 7));
  const [data, setData] = useState<ContactsGrowthView | null>(null);
  const [failed, setFailed] = useState(false);
  const fmt = useMemo(
    () => new Intl.NumberFormat(locale === "ar" ? "ar-u-nu-latn" : "en"),
    [locale],
  );

  useEffect(() => {
    let live = true;
    setData(null);
    setFailed(false);
    hubFetch<ContactsGrowthView>(
      `/v1/workspaces/${workspaceId}/analytics/contacts-growth?month=${encodeURIComponent(month)}`,
    ).then(
      (d) => live && setData(d),
      () => live && setFailed(true),
    );
    return () => {
      live = false;
    };
  }, [workspaceId, month]);

  const max = data ? Math.max(1, ...data.days.map((d) => Math.max(d.created, d.optedOut))) : 1;
  const net = data ? data.totalCreated - data.totalOptedOut : 0;
  return (
    <section
      aria-label={t("dashboard.home.growthCard.title")}
      data-testid="home-contacts-growth"
      className="flex flex-col gap-3 rounded-2xl border border-app-line bg-app-surface p-4"
    >
      <div className="flex flex-wrap items-center justify-between gap-2">
        <h3 className="text-lg font-semibold text-fg">{t("dashboard.home.growthCard.title")}</h3>
        <label className="flex items-center gap-2 text-sm text-fg-muted">
          {t("dashboard.home.growthCard.month")}
          <input
            type="month"
            value={month}
            max={new Date().toISOString().slice(0, 7)}
            onChange={(e) => e.target.value && setMonth(e.target.value)}
            className="rounded border border-border bg-surface px-2 py-1 text-fg"
            data-testid="home-growth-month"
          />
        </label>
      </div>
      {failed ? (
        <p className="text-sm text-fg-muted">{t("dashboard.home.growthCard.unavailable")}</p>
      ) : !data ? (
        <p className="text-sm text-fg-muted">{t("dashboard.home.growthCard.loading")}</p>
      ) : (
        <>
          <dl className="grid gap-3 [grid-template-columns:repeat(auto-fit,minmax(8rem,1fr))]">
            <div>
              <dt className="text-xs text-fg-muted">{t("dashboard.home.growthCard.new")}</dt>
              <dd data-testid="home-growth-new" className="text-xl font-semibold text-fg">
                {fmt.format(data.totalCreated)}
              </dd>
            </div>
            <div>
              <dt className="text-xs text-fg-muted">{t("dashboard.home.growthCard.lost")}</dt>
              <dd data-testid="home-growth-lost" className="text-xl font-semibold text-fg">
                {fmt.format(data.totalOptedOut)}
              </dd>
            </div>
            <div>
              <dt className="text-xs text-fg-muted">{t("dashboard.home.growthCard.net")}</dt>
              <dd data-testid="home-growth-net" className="text-xl font-semibold text-fg">
                {fmt.format(net)}
              </dd>
            </div>
          </dl>
          {data.totalCreated === 0 && data.totalOptedOut === 0 ? (
            <p className="text-sm text-fg-muted">{t("dashboard.home.growthCard.empty")}</p>
          ) : (
            <div
              role="img"
              aria-label={t("dashboard.home.growthCard.trend", { month })}
              className="flex h-24 items-end gap-px"
              dir="ltr"
            >
              {data.days.map((d) => (
                <div
                  key={d.date}
                  title={t("dashboard.home.growthCard.dayLabel", {
                    date: d.date,
                    created: fmt.format(d.created),
                    lost: fmt.format(d.optedOut),
                  })}
                  className="flex h-full flex-1 items-end gap-px"
                >
                  <span
                    className="flex-1 bg-primary"
                    style={{ height: `${(d.created / max) * 100}%` }}
                  />
                  <span
                    className="flex-1 bg-danger"
                    style={{ height: `${(d.optedOut / max) * 100}%` }}
                  />
                </div>
              ))}
            </div>
          )}
        </>
      )}
    </section>
  );
}
